// ========================================
// Collection Filters & Sorters Manager
// ========================================

const erjCollectionPanelState = {
  isSorterOpen: false,
  activeFilterItems: new Set(),
  initialFilterStates: new Map(),
  tempSelectedFilters: new Map(),
  initialPriceMin: null,
  initialPriceMax: null,
};

function isFilterPanelOpen() {
  return document.querySelector(".erj-collection-filter-panel-overlay")?.classList.contains("active")
}

// Global manager instances for cross-communication
let erjCollectionSorterManager = null;
let erjCollectionFilterManager = null;

// Utility Functions
function _debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Global control methods for mutual exclusion
function erjCollectionCloseSorter() {
  if (erjCollectionSorterManager && isFilterPanelOpen()) {
    erjCollectionSorterManager.closeSorter();
  }
}

function erjCollectionCloseFilter() {
  if (erjCollectionFilterManager && isFilterPanelOpen()) {
    erjCollectionFilterManager.closePanel();
  }
}

// ========================================
// Sorter Management Class
// ========================================
class ErcSorterManager {
  constructor() {
    this.searchParams = new URLSearchParams(window.location.search);
    this.sortParam = this.searchParams.get("sort_by");
    this.initElements();
    this.bindEvents();
    this.setSelectedOption();
    this.boundHandleDocumentClick = this._handleDocumentClick.bind(this);
  }

  initElements() {
    this.erjCollectionSorter = document.querySelector(".erj-collection-sorter");
    if (!this.erjCollectionSorter) {
      return;
    }

    this.erjCollectionSorterTitle = this.erjCollectionSorter.querySelector(
      ".erj-collection-sorter-title"
    );
    this.erjCollectionSorterDropdown = this.erjCollectionSorter.querySelector(
      ".erj-collection-sorter-dropdown"
    );
    this.erjCollectionSorterOptions =
      this.erjCollectionSorterDropdown?.querySelectorAll("li");
  }

  bindEvents() {
    if (!this.erjCollectionSorterTitle || !this.erjCollectionSorterDropdown)
      return;

    this.erjCollectionSorterTitle.addEventListener("click", () => {
      this.toggleDropdown();
    });

    this.erjCollectionSorterDropdown.querySelector("ul")?.addEventListener(
      "click",
      (e) => {
        const target = e.target;

        if (
          !target?.classList?.contains("erj-collection-sorter-dropdown-item")
        ) {
          return;
        }

        const sortParam = target.dataset.value;
        this.updateURL(sortParam);
      }
    );

    document.addEventListener("click", this.boundHandleDocumentClick, true);
  }

  _handleDocumentClick(event) {
    if (
      erjCollectionPanelState.isSorterOpen &&
      this.erjCollectionSorter &&
      !this.erjCollectionSorter.contains(event.target)
    ) {
      this.closeSorter();
    }
  }

  _animateOpen() {
    const dropdown = this.erjCollectionSorterDropdown;
    if (!dropdown) return;

    dropdown.style.opacity = "0";
    dropdown.style.height = "auto";

    requestAnimationFrame(() => {
      const fullHeight = dropdown.scrollHeight;
      dropdown.style.height = "0px";

      requestAnimationFrame(() => {
        dropdown.style.height = fullHeight + "px";
        dropdown.style.opacity = "1";
      });
    });
  }

  _animateClose() {
    const dropdown = this.erjCollectionSorterDropdown;
    if (!dropdown) return;

    dropdown.style.height = dropdown.scrollHeight + "px";

    requestAnimationFrame(() => {
      dropdown.style.height = "0px";
      dropdown.style.opacity = "0";
    });
  }

  toggleDropdown() {
    const dropdown = this.erjCollectionSorterDropdown;
    if (!dropdown) return;

    if (!erjCollectionPanelState.isSorterOpen) {
      erjCollectionCloseFilter();
      erjCollectionPanelState.isSorterOpen = true;
      dropdown.classList.add("active");
      this.erjCollectionSorterTitle?.classList.add("active");
      this._animateOpen();
      document.addEventListener("click", this.boundHandleDocumentClick, true);
    } else {
      this.closeSorter();
    }
  }

  closeSorter() {
    const dropdown = this.erjCollectionSorterDropdown;

    this.erjCollectionSorterTitle?.classList.remove("active");

    if (!erjCollectionPanelState.isSorterOpen) {
      document.removeEventListener(
        "click",
        this.boundHandleDocumentClick,
        true
      );
      if (dropdown && dropdown.classList.contains("active")) {
        dropdown.classList.remove("active");
        dropdown.style.height = "0px";
        dropdown.style.opacity = "0";
      }
      return;
    }

    erjCollectionPanelState.isSorterOpen = false;
    document.removeEventListener("click", this.boundHandleDocumentClick, true);

    if (dropdown) {
      this._animateClose();
      dropdown.classList.remove("active");
    }
  }

  updateURL(sortParam) {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("sort_by", sortParam);
    window.location.href = newUrl.toString();
  }

  setSelectedOption() {
    if (!this.erjCollectionSorterOptions) return;

    [...this.erjCollectionSorterOptions].forEach((option) => {
      if (option.dataset.value === this.sortParam) {
        option.classList.add("selected");
      }
    });
  }
}

// ========================================
// Filter Panel Management Class
// ========================================
class ErcFilterPanelManager {
  constructor() {
    this.initElements();
    this.bindEvents();
    this.PRICE_PARAM_MIN = "filter.v.price.gte";
    this.PRICE_PARAM_MAX = "filter.v.price.lte";
  }

  initElements() {
    this.erjCollectionFilterToggle = document.querySelector(
      ".erj-collection-filter-title"
    );
    this.erjCollectionFilterOverlay = document.querySelector(
      ".erj-collection-filter-panel-overlay"
    );
    this.erjCollectionFilterPanel =
      this.erjCollectionFilterOverlay?.querySelector(
        ".erj-collection-filter-panel"
      );
    this.erjCollectionFilterClose =
      this.erjCollectionFilterPanel?.querySelector(
        ".erj-collection-filter-panel-close"
      );
    this.erjCollectionFilterReset =
      this.erjCollectionFilterPanel?.querySelector(
        ".erj-collection-filter-panel-reset"
      );
    this.erjCollectionFilterApply =
      this.erjCollectionFilterPanel?.querySelector(
        ".erj-collection-filter-panel-footer-apply"
      );

    this.priceRangeMinEl = document.getElementById("price-range-min");
    this.priceRangeMaxEl = document.getElementById("price-range-max");
    this.priceInputMinEl = document.getElementById("price-input-min");
    this.priceInputMaxEl = document.getElementById("price-input-max");
    this.priceRangeSelectedTrackEl = document.querySelector(
      ".price-range-selected-track"
    );
  }

  updatePriceVisuals(minVal, maxVal, rangeMax) {
    if (
      !this.priceRangeMinEl ||
      !this.priceRangeMaxEl ||
      !this.priceInputMinEl ||
      !this.priceInputMaxEl ||
      !this.priceRangeSelectedTrackEl
    ) {
      return;
    }
    const min = parseFloat(minVal);
    const max = parseFloat(maxVal);
    const rMax = parseFloat(rangeMax);

    this.priceRangeMinEl.value = min;
    this.priceRangeMaxEl.value = max;
    this.priceInputMinEl.value = min;
    this.priceInputMaxEl.value = max;

    if (rMax > 0) {
      const leftPercent = Math.round((min / rMax) * 1000) / 10;
      const widthPercent = Math.round(((max - min) / rMax) * 1000) / 10;
      this.priceRangeSelectedTrackEl.style.left = `${leftPercent}%`;
      this.priceRangeSelectedTrackEl.style.width = `${widthPercent}%`;
    } else {
      this.priceRangeSelectedTrackEl.style.left = "0%";
      this.priceRangeSelectedTrackEl.style.width = "0%";
    }
  }

  bindPriceFilterEvents() {
    if (
      !this.priceRangeMinEl ||
      !this.priceRangeMaxEl ||
      !this.priceInputMinEl ||
      !this.priceInputMaxEl
    ) {
      return;
    }

    const handleRangeInput = (event) => {
      let minVal = parseFloat(this.priceRangeMinEl.value);
      let maxVal = parseFloat(this.priceRangeMaxEl.value);
      const currentRangeMax = parseFloat(this.priceRangeMinEl.max);

      const activeEl = event.target;

      if (activeEl === this.priceRangeMinEl && minVal > maxVal) {
        maxVal = minVal;
        this.priceRangeMaxEl.value = maxVal;
      } else if (activeEl === this.priceRangeMaxEl && maxVal < minVal) {
        minVal = maxVal;
        this.priceRangeMinEl.value = minVal;
      }

      this.updatePriceVisuals(minVal, maxVal, currentRangeMax);
      erjCollectionPanelState.tempSelectedFilters.set(
        this.PRICE_PARAM_MIN,
        new Set([minVal.toString()])
      );
      erjCollectionPanelState.tempSelectedFilters.set(
        this.PRICE_PARAM_MAX,
        new Set([maxVal.toString()])
      );
    };

    const handleNumberInput = (event) => {
      let minVal = parseFloat(this.priceInputMinEl.value);
      let maxVal = parseFloat(this.priceInputMaxEl.value);
      const currentRangeMax = parseFloat(this.priceRangeMinEl.max);

      if (isNaN(minVal)) minVal = 0;
      if (isNaN(maxVal)) maxVal = currentRangeMax;

      minVal = Math.max(0, Math.min(minVal, currentRangeMax));
      maxVal = Math.max(0, Math.min(maxVal, currentRangeMax));

      if (minVal > maxVal) {
        if (event.target === this.priceInputMinEl) {
          maxVal = minVal;
        } else {
          minVal = maxVal;
        }
      }
      this.updatePriceVisuals(minVal, maxVal, currentRangeMax);
      erjCollectionPanelState.tempSelectedFilters.set(
        this.PRICE_PARAM_MIN,
        new Set([minVal.toString()])
      );
      erjCollectionPanelState.tempSelectedFilters.set(
        this.PRICE_PARAM_MAX,
        new Set([maxVal.toString()])
      );
    };

    this.priceRangeMinEl.addEventListener(
      "input",
      _debounce(handleRangeInput, 10)
    );
    this.priceRangeMaxEl.addEventListener(
      "input",
      _debounce(handleRangeInput, 10)
    );
    this.priceInputMinEl.addEventListener(
      "input",
      _debounce(handleNumberInput, 100)
    );
    this.priceInputMaxEl.addEventListener(
      "input",
      _debounce(handleNumberInput, 100)
    );
  }

  bindEvents() {
    if (!this.erjCollectionFilterToggle || !this.erjCollectionFilterOverlay)
      return;

    this.erjCollectionFilterToggle.addEventListener("click", () => {
      this.openPanel();
    });

    this.erjCollectionFilterOverlay.addEventListener("click", (e) => {
      if (e.target === this.erjCollectionFilterOverlay) {
        this.applyFilters();
        this.closePanel(false);
      }
    });

    this.erjCollectionFilterClose?.addEventListener("click", () => {
      this.closePanel(false);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && isFilterPanelOpen()) {
        this.closePanel(false);
      }
    });

    this.erjCollectionFilterReset?.addEventListener("click", () => {
      this.resetFilters();
    });

    this.erjCollectionFilterApply?.addEventListener("click", () => {
      this.applyFilters();
    });

    this.bindPriceFilterEvents();
  }

  storeInitialFilterStates() {
    erjCollectionPanelState.initialFilterStates.clear();
    const checkboxButtons = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item button"
    );
    checkboxButtons.forEach((button) => {
      const paramName = button.dataset.filterParamName;
      const value = button.dataset.filterValue;
      if (paramName && value) {
        const key = `${paramName}|${value}`;
        erjCollectionPanelState.initialFilterStates.set(
          key,
          button.classList.contains("active")
        );
      }
    });
    if (this.priceInputMinEl && this.priceInputMaxEl) {
      erjCollectionPanelState.initialPriceMin = parseFloat(
        this.priceInputMinEl.value
      );
      erjCollectionPanelState.initialPriceMax = parseFloat(
        this.priceInputMaxEl.value
      );
    }
  }

  resetTempSelectedFilters() {
    erjCollectionPanelState.tempSelectedFilters.clear();
    erjCollectionPanelState.initialFilterStates.forEach((isActive, key) => {
      if (isActive) {
        const [paramName, value] = key.split("|");
        if (!erjCollectionPanelState.tempSelectedFilters.has(paramName)) {
          erjCollectionPanelState.tempSelectedFilters.set(paramName, new Set());
        }
        erjCollectionPanelState.tempSelectedFilters.get(paramName).add(value);
      }
    });
  }

  restoreFilterStatesToInitial() {
    const checkboxButtons = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item button"
    );
    checkboxButtons.forEach((button) => {
      const paramName = button.dataset.filterParamName;
      const value = button.dataset.filterValue;
      if (paramName && value) {
        const key = `${paramName}|${value}`;
        const isActiveInitial =
          erjCollectionPanelState.initialFilterStates.get(key);
        if (isActiveInitial) {
          button.classList.add("active");
          button.setAttribute("aria-checked", "true");
        } else {
          button.classList.remove("active");
          button.setAttribute("aria-checked", "false");
        }
      }
    });
    this.resetTempSelectedFilters();
  }

  openPanel() {
    if (isFilterPanelOpen()) return;
    let filterLayout = document.querySelector(".erj-collection-filter-panel")
    let windowHeight = window.innerHeight;
    filterLayout.style.height = (windowHeight) + 'px';
    
    erjCollectionCloseSorter();

    this.storeInitialFilterStates();
    this.resetTempSelectedFilters();

    if (window.erjPriceFilterData && this.priceRangeMinEl) {
      const { rangeMax, currentMin, currentMax } = window.erjPriceFilterData;
      this.priceRangeMinEl.max = rangeMax;
      this.priceRangeMaxEl.max = rangeMax;
      this.priceInputMinEl.max = rangeMax;
      this.priceInputMaxEl.max = rangeMax;

      this.updatePriceVisuals(currentMin, currentMax, rangeMax);

      const minP = parseFloat(currentMin);
      const maxP = parseFloat(currentMax);
      const rMaxP = parseFloat(rangeMax);

      if (minP > 0 || maxP < rMaxP) {
        erjCollectionPanelState.tempSelectedFilters.set(
          this.PRICE_PARAM_MIN,
          new Set([minP.toString()])
        );
        erjCollectionPanelState.tempSelectedFilters.set(
          this.PRICE_PARAM_MAX,
          new Set([maxP.toString()])
        );
      }
    }

    this.erjCollectionFilterOverlay.classList.add("active");
    this.erjCollectionFilterToggle?.classList.add("active");
    this.lockBodyScroll();
  }

  closePanel(isApplyingFilters = false) {
    if (!isFilterPanelOpen()) return;

    if (!isApplyingFilters) {
      this.restoreFilterStatesToInitial();
      if (
        this.priceRangeMinEl &&
        erjCollectionPanelState.initialPriceMin !== null &&
        erjCollectionPanelState.initialPriceMax !== null
      ) {
        const rangeMax = parseFloat(this.priceRangeMinEl.max);
        this.updatePriceVisuals(
          erjCollectionPanelState.initialPriceMin,
          erjCollectionPanelState.initialPriceMax,
          rangeMax
        );
        if (
          erjCollectionPanelState.initialPriceMin > 0 ||
          erjCollectionPanelState.initialPriceMax < rangeMax
        ) {
          erjCollectionPanelState.tempSelectedFilters.set(
            this.PRICE_PARAM_MIN,
            new Set([erjCollectionPanelState.initialPriceMin.toString()])
          );
          erjCollectionPanelState.tempSelectedFilters.set(
            this.PRICE_PARAM_MAX,
            new Set([erjCollectionPanelState.initialPriceMax.toString()])
          );
        } else {
          erjCollectionPanelState.tempSelectedFilters.delete(
            this.PRICE_PARAM_MIN
          );
          erjCollectionPanelState.tempSelectedFilters.delete(
            this.PRICE_PARAM_MAX
          );
        }
      }
    }

    this.erjCollectionFilterOverlay.classList.remove("active");
    this.erjCollectionFilterToggle?.classList.remove("active");
    this.unlockBodyScroll();
  }

  togglePanel() {
    if (isFilterPanelOpen()) {
      this.closePanel(false);
    } else {
      this.openPanel();
    }
  }

  handleEscapeKey(e) {
    if (e.key === "Escape" && isFilterPanelOpen()) {
      this.closePanel(false);
    }
  }

  lockBodyScroll() {
    document.body.style.overflow = "hidden";
  }

  unlockBodyScroll() {
    document.body.style.overflow = "";
  }

  resetFilters() {
    const checkboxButtons = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item button"
    );
    checkboxButtons.forEach((button) => {
      button.classList.remove("active");
      button.setAttribute("aria-checked", "false");
    });
    erjCollectionPanelState.tempSelectedFilters.clear();

    if (this.priceRangeMinEl && window.erjPriceFilterData) {
      const defaultMin = 0;
      const defaultMax = window.erjPriceFilterData.rangeMax;
      this.updatePriceVisuals(defaultMin, defaultMax, defaultMax);
    }

    const baseUrl = window.location.pathname;
    const currentParams = new URLSearchParams(window.location.search);
    let finalUrl = baseUrl;
    if (currentParams.has("sort_by")) {
      finalUrl += `?sort_by=${currentParams.get("sort_by")}`;
    }

    history.replaceState({}, document.title, finalUrl);
    this.searchAndRender(finalUrl);
    this.closePanel(true);
  }

  applyFilters() {
    const baseUrl = window.location.pathname;
    const newParams = new URLSearchParams(window.location.search);

    const managedFilterParams = new Set();
    managedFilterParams.add(this.PRICE_PARAM_MIN);
    managedFilterParams.add(this.PRICE_PARAM_MAX);

    erjCollectionPanelState.initialFilterStates.forEach((_, key) => {
      const paramName = key.split("|")[0];
      managedFilterParams.add(paramName);
    });

    erjCollectionPanelState.tempSelectedFilters.forEach((_, paramName) => {
      managedFilterParams.add(paramName);
    });

    managedFilterParams.forEach((paramName) => {
      newParams.delete(paramName);
    });

    if (
      this.priceInputMinEl &&
      this.priceInputMaxEl &&
      window.erjPriceFilterData
    ) {
      const minVal = parseFloat(this.priceInputMinEl.value);
      const maxVal = parseFloat(this.priceInputMaxEl.value);
      const rangeMax = window.erjPriceFilterData.rangeMax;
      if (minVal === 0 && maxVal === rangeMax) {
        erjCollectionPanelState.tempSelectedFilters.delete(
          this.PRICE_PARAM_MIN
        );
        erjCollectionPanelState.tempSelectedFilters.delete(
          this.PRICE_PARAM_MAX
        );
      }
    }

    erjCollectionPanelState.tempSelectedFilters.forEach((values, paramName) => {
      values.forEach((value) => {
        newParams.append(paramName, value);
      });
    });

    const newUrl = `${baseUrl}?${newParams.toString()}`;
    history.replaceState({}, document.title, newUrl);
    this.searchAndRender(newUrl);
    this.closePanel(true);
  }

  searchAndRender(queryUrl) {
    const u = new URL(window.location.origin + queryUrl);
    if (document.getElementById("default-collection-v2-section")?.dataset?.id) {
      u.searchParams.set("section_id", document.getElementById("default-collection-v2-section").dataset.id);
    }

    fetch(u.toString())
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const productGrid = document.getElementsByClassName("erj-collection-wrapper")?.[0];
        const newProductGrid = doc.getElementsByClassName("erj-collection-wrapper")?.[0];
        if (!newProductGrid?.innerHTML) {
          return;
        }

        productGrid.innerHTML = newProductGrid.innerHTML;
        _erjCollectionInit();
        runAsyncActions();
        setLoadMore();
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }
}

// ========================================
// Filter Item Management Class
// ========================================
class ErcFilterItemManager {
  constructor() {
    this.initElements();
    this.bindEvents();
  }

  initElements() {
    this.erjCollectionFilterItems = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item"
    );
    this.erjCollectionFilterCheckboxOptions = document.querySelectorAll(
      ".erj-collection-filter-panel-filter-item-checkbox-item"
    );
    this.erjCollectionFilterShowMore = document.querySelectorAll(".show-more-btn");
    this.erjCollectionFilterShowLess = document.querySelectorAll(".show-less-btn");
  }

  bindEvents() {
    this.erjCollectionFilterItems.forEach((item) => {
      const itemTitle = item.querySelector(
        ".erj-collection-filter-panel-filter-item-title"
      );

      if (!itemTitle) return;

      itemTitle.addEventListener(
        "click",
        () => {
          this.toggleItem(item);
        }
      );
    });

    this.erjCollectionFilterCheckboxOptions.forEach((option) => {
      option.addEventListener("click", (event) => {
        const button = event.currentTarget.querySelector("button");
        this.handleCheckboxClick(button);
      });
    });

    this.erjCollectionFilterShowMore.forEach(btn=>{
      btn.addEventListener("click", event => {
        this.showMoreEvent(btn)
      })
    })

    this.erjCollectionFilterShowLess.forEach(btn => {
      btn.addEventListener("click", event =>{
        this.showLessEvent(btn)
      })
    })
    
  }

  handleCheckboxClick(button) {
    const paramName = button.dataset.filterParamName;
    const value = button.dataset.filterValue;

    button.classList.toggle("active");
    button.setAttribute("aria-checked", button.classList.contains("active"));

    if (!erjCollectionPanelState.tempSelectedFilters.has(paramName)) {
      erjCollectionPanelState.tempSelectedFilters.set(paramName, new Set());
    }

    if (button.classList.contains("active")) {
      erjCollectionPanelState.tempSelectedFilters.get(paramName).add(value);
    } else {
      erjCollectionPanelState.tempSelectedFilters.get(paramName).delete(value);
    }

    this.updateOptions()
  }

  updateFilterContentHeight(checkboxItem) {
    const itemContent = checkboxItem?.parentNode;

    if (!itemContent?.style?.height) {
      return;
    }

    itemContent.style.height = "auto";
  }

  updateOptions() {
    const baseUrl = window.location.pathname;
    const newParams = new URLSearchParams(window.location.search);

    const managedFilterParams = new Set();
    managedFilterParams.add(this.PRICE_PARAM_MIN);
    managedFilterParams.add(this.PRICE_PARAM_MAX);

    erjCollectionPanelState.initialFilterStates.forEach((_, key) => {
      const paramName = key.split("|")[0];
      managedFilterParams.add(paramName);
    });

    erjCollectionPanelState.tempSelectedFilters.forEach((_, paramName) => {
      managedFilterParams.add(paramName);
    });

    managedFilterParams.forEach((paramName) => {
      newParams.delete(paramName);
    });

    erjCollectionPanelState.tempSelectedFilters.forEach((values, paramName) => {
      values.forEach((value) => {
        newParams.append(paramName, value);
      });
    });

    const newUrl = `${baseUrl}?${newParams.toString()}`;
    const u = new URL(window.location.origin + newUrl);
    if (document.getElementById("default-collection-v2-section")?.dataset?.id) {
      u.searchParams.set("section_id", document.getElementById("default-collection-v2-section").dataset.id);
    }

    fetch(u.toString())
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const newFilterItems = doc.querySelectorAll(".erj-collection-filter-panel-filter-item");
        const currentFilterItems = document.querySelectorAll(".erj-collection-filter-panel-filter-item");

        currentFilterItems.forEach((item) => {
          const title = item.dataset.title;
          const filterItem = newFilterItems.values().find((newItem) => {
            return newItem.dataset.title === title;
          });

          if (!filterItem) {
            return;
          }
          
          if (filterItem.classList.contains("hidden")) {
            item.classList.add("hidden");
          } else {
            item.classList.remove("hidden");
          }

          const newShowMoreBtn = filterItem.querySelector(".show-more-btn");
          const currentShowMoreBtn = item.querySelector(".show-more-btn");
          const currentShowLessBtn = currentShowMoreBtn?.parentElement?.querySelector(".show-less-btn")
          if (newShowMoreBtn?.classList?.contains("hidden")){
            // reset show more
            this.showLessEvent(currentShowLessBtn);
            currentShowMoreBtn?.classList?.add("hidden")
          } else if (currentShowLessBtn?.classList?.contains("hidden")){
            currentShowMoreBtn?.classList?.remove("hidden")
          }
        });

        const newCheckboxItems = doc.querySelectorAll(".erj-collection-filter-panel-filter-item-checkbox-item");
        const currentCheckboxItems = document.querySelectorAll(".erj-collection-filter-panel-filter-item-checkbox-item");
        currentCheckboxItems.forEach((item) => {
          const value = item.dataset.value;
          const checkboxItem = newCheckboxItems.values().find((newItem) => {
            return newItem.dataset.value === value;
          });

          if (!checkboxItem) {
            return;
          }
          
          if (checkboxItem.classList.contains("hidden")) {
            item.classList.add("hidden");
          } else {
            item.classList.remove("hidden");
          }

          item.querySelector('.erj-collection-filter-panel-filter-item-count').innerHTML = checkboxItem.querySelector(".erj-collection-filter-panel-filter-item-count").innerHTML;

          this.updateFilterContentHeight(item);
        });
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
    
  }

  getItemId(item) {
    return (
      item?.dataset?.filterId ||
      item?.querySelector(".erj-collection-filter-panel-filter-item-title")
        ?.textContent ||
      "unknown"
    );
  }

  expandItemWithRAF(item, filterContent,startHeight = 0) {
    const itemId = this.getItemId(item);

    filterContent.style.height = "auto";

    requestAnimationFrame(() => {
      const fullHeight = filterContent.scrollHeight;
      filterContent.style.height = startHeight + "px";
      requestAnimationFrame(() => {
        filterContent.style.height = fullHeight + "px";
        filterContent.style.opacity = "1";
      });
    });
  }

  collapseItemWithRAF(item, filterContent,endHeight = 0) {
    const itemId = this.getItemId(item);

    filterContent.style.height = endHeight + "px";
    filterContent.style.opacity = "0";
  }

  toggleItem(item) {
    const filterContent = item.querySelector(
      ".erj-collection-filter-panel-filter-item-content"
    );
    if (!filterContent) return;

    const isActive = item.classList.contains("active");
    const itemId = this.getItemId(item);
    if (isActive) {
      this.collapseItemWithRAF(item, filterContent);
      erjCollectionPanelState.activeFilterItems.delete(itemId);
    } else {
      this.expandItemWithRAF(item, filterContent);
      erjCollectionPanelState.activeFilterItems.add(itemId);
    }

    item.classList.toggle("active");
  }

  showMoreEvent (btn) {
    btn.classList?.add("hidden");
    btn.parentElement?.querySelector(".show-less-btn")?.classList?.remove("hidden");
    btn.parentElement?.querySelectorAll(".show-more-item")?.forEach(el => {
      el.style.display = 'block';
      el.style.height = 'auto';
    })

    const filterContent = btn.parentElement;
    if (filterContent) {
      this.expandItemWithRAF(_,filterContent,filterContent.style.height);
    }
  }
  
  showLessEvent (btn) {
    btn.classList?.add("hidden");
    btn.parentElement?.querySelector(".show-more-btn")?.classList?.remove("hidden");
    const options = btn.parentElement?.querySelectorAll(".show-more-item");
    options.forEach(el => {
      el.style.display = 'none';
    })
    const filterContent = btn.parentElement;
    if (filterContent) {
      filterContent.style.height = "fit-content";
    }
  }
}

function _bindSelectedFilterRemoveEvents() {
  const selectedFiltersContainer = document.querySelector(
    ".erj-collection-selected-filters"
  );

  if (!selectedFiltersContainer) {
    return;
  }

  selectedFiltersContainer.addEventListener("click", function (event) {
    const filterItem = event.target.closest(".erj-collection-selected-filter-item");

    if (filterItem) {
      const removeIcon = filterItem.querySelector(".selected-filter-item-remove");

      if (!removeIcon) {
        return;
      }

      const urlToRemove = removeIcon?.dataset?.urlToRemove;

      if (
        urlToRemove &&
        typeof urlToRemove === "string" &&
        urlToRemove.trim() !== ""
      ) {
        history.replaceState({}, document.title, urlToRemove);
        erjCollectionFilterManager.searchAndRender(urlToRemove);
      } else {
        console.warn(
          "[Collection Filters] Clicked .selected-filter-item-remove, but data-url-to-remove was invalid or missing:",
          urlToRemove
        );
      }
    }
  });
}

function _erjCollectionInit() {
  try {
    erjCollectionSorterManager = new ErcSorterManager();
    erjCollectionFilterManager = new ErcFilterPanelManager();
    new ErcFilterItemManager();
    _bindSelectedFilterRemoveEvents();
  } catch (error) {
    console.error(
      `[Collection Filters] Initialization error: ${error.message}`
    );
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", _erjCollectionInit);
} else {
  _erjCollectionInit();
}
